import { binHas } from './bins.ts';
import { cellAtWorld, heroWalkable } from '../grid.ts';
import { imax, imin, mulBp, withinRange } from '../math/fixed.ts';
import type { BattleState, CragRules, CragState, SendaNpc } from '../types.ts';
import { damageHero, ev, findEnemy } from './combat.ts';
import { stepToward } from './enemies.ts';
import { cubOnPerson, cubSpawn, cubStep, cubsAlive } from './cubs.ts';

/**
 * The ledge of the wind (docs/sendas/guerrero.md, chapter 5, act 1: «La cornisa del viento»). A ledge two hexes wide,
 * one in parts, up a cliff to the pass's mouth. Harl goes ahead, slow, the banner tied on his back; the wind comes in
 * gusts toward the drop, seen `warn` before (the snow lifting off the ledge, as on the cut bridge). Harl and Wenn know
 * the ledge: at the warning they kneel and lean into the rock, and the gust passes over them. A gust catching the hero
 * in the open pushes it to the drop's lip and strikes; in a niche of the wall it passes. Cubs of the Ventisquero come
 * at the walkers from ahead and behind (low and round, the gusts roll over them). Won with Harl at the pass's
 * mouth; lost with the hero, Harl or Wenn down.
 *
 * Cost: a gust looks at the hero and the cubs once; the cubs walk the ledge's middle line (no path finding).
 */

const fresh = (): CragState => ({ way: 1, gust: 0, cub: 0, cubs: [] });

const npc = (s: BattleState, id: string): SendaNpc | undefined => s.senda?.npcs.find((n) => n.id === id && n.hp > 0);

/** A gust's warning is out (the next one strikes within `warn`). */
export const cragWarn = (s: BattleState, r: CragRules, w: CragState): boolean => {
  const t = r.plan[w.gust];
  return t !== undefined && s.waveT >= t - r.warn;
};

/** Ticks to the next gust (−1 none left). */
export const cragNext = (s: BattleState, r: CragRules, w: CragState): number => {
  const t = r.plan[w.gust];
  return t === undefined ? -1 : imax(0, t - s.waveT);
};

/** In a niche of the wall (the gust misses it). */
export const cragSheltered = (s: BattleState, r: CragRules, x: number, y: number): boolean => binHas(r.niches, cellAtWorld(s, x, y));

/** Pushed `d` toward the drop (+x), as far as the ledge goes. */
const toLip = (s: BattleState, o: { x: number; y: number }, d: number): void => {
  let x = o.x;
  for (let k = 0; k < d; k += 100) {
    const c = cellAtWorld(s, x + 100, o.y);
    if (c < 0 || !heroWalkable(s, c)) break;
    x += 100;
  }
  o.x = x;
};

const gust = (s: BattleState, r: CragRules): void => {
  ev(s, { t: 'senda', kind: 'gust', npc: '', x: 0, y: 0, n: 1 });
  for (const h of s.heroes) {
    if (h.downTicks > 0 || cragSheltered(s, r, h.x, h.y)) continue;
    damageHero(s, h, mulBp(h.stats.maxHp, r.heroBp), null, 'physical', true);
    toLip(s, h, r.push);
    h.destX = h.ax = h.x;
    h.destY = h.ay = h.y;
    h.path = [];
    h.moving = false;
    h.stun = imax(h.stun, r.stun);
    ev(s, { t: 'senda', kind: 'swept', npc: '', x: h.x, y: h.y });
  }
};

/** The nearest point of the way to a y (its index). */
const wayAt = (r: CragRules, y: number): number => {
  let best = 0;
  let bd = 0x7fffffff;
  r.way.forEach((p, i) => {
    const d = Math.abs(p.y - y);
    if (d < bd) {
      bd = d;
      best = i;
    }
  });
  return best;
};

export const cragTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = s.rules?.senda?.crag;
  const st = s.senda;
  if (!r || !st || !s.started) return;
  const w = (st.crag ??= fresh());
  const harl = npc(s, 'harlOld');
  const wenn = npc(s, 'wenn');
  const h = s.heroes.find((x) => x.downTicks === 0);
  const warn = cragWarn(s, r, w);
  if (warn && w.gust === 0) cue('warn1');
  const t = r.plan[w.gust];
  if (t !== undefined && s.waveT >= t) {
    gust(s, r);
    w.gust++;
    cue(`gust${w.gust}`);
  }
  // Harl: on along the way (waiting for the hero), kneeling while the wind warns.
  if (harl) {
    harl.moving = false;
    const p = r.way[w.way];
    if (!warn && p && h && withinRange(h.x, h.y, harl.x, harl.y, r.wait)) {
      harl.moving = true;
      if (stepToward(harl, p.x, p.y, r.pace)) w.way = imin(r.way.length - 1, w.way + 1);
    }
    if (w.way * 2 >= r.way.length) cue('half');
  }
  // Wenn: behind the hero, a hex and a half, kneeling at the warning.
  if (wenn && h) {
    wenn.moving = false;
    if (!warn && !withinRange(h.x, h.y, wenn.x, wenn.y, 1500)) {
      wenn.moving = true;
      const i = wayAt(r, h.y);
      const back = r.way[imax(0, i - 1)] as { x: number; y: number };
      stepToward(wenn, back.x, back.y, r.pace + 10);
    }
  }
  // The cubs: from the plan, at the mouth or the foot, walking the ledge's middle to the nearest walker.
  const plan = r.cubs[w.cub];
  if (plan && s.waveT >= plan.t) {
    w.cub++;
    const at = plan.top ? r.mouth : (r.way[0] as { x: number; y: number });
    cubSpawn(s, w.cubs, { x: at.x, y: at.y }, plan.top ? -10 : -11, r.cub.hp);
  }
  cubsAlive(s, w.cubs);
  for (const c of w.cubs) {
    const e = findEnemy(s, c.id);
    if (!e) continue;
    const goal = harl && (c.to === -10 || !wenn) ? harl : (wenn ?? harl);
    if (!goal) continue;
    if (withinRange(e.x, e.y, goal.x, goal.y, 500)) {
      cubOnPerson(s, e, goal, r.cub.blows);
      continue;
    }
    // Along the ledge's middle toward the walker's row, then at it.
    const i = wayAt(r, e.y);
    const j = wayAt(r, goal.y);
    const p = i === j ? goal : (r.way[i + (j > i ? 1 : -1)] as { x: number; y: number });
    cubStep(s, e, p.x, p.y, r.cub, 40);
  }
};

/** Won with Harl at the mouth. */
export const cragResult = (s: BattleState): 'won' | null => {
  const r = s.rules?.senda?.crag;
  const harl = npc(s, 'harlOld');
  return r && harl && withinRange(harl.x, harl.y, r.mouth.x, r.mouth.y, r.reach) ? 'won' : null;
};

/** How far Harl is up the ledge. */
export const cragGoal = (s: BattleState): { done: number; need: number } => {
  const r = s.rules?.senda?.crag;
  const w = s.senda?.crag;
  if (!r || !w) return { done: 0, need: 1 };
  return { done: w.way, need: imax(1, r.way.length - 1) };
};

/** The cubs are the act's own: it walks them (true). */
export const cragHold = (s: BattleState, e: { id: number }): boolean => !!s.senda?.crag?.cubs.some((c) => c.id === e.id);
